import { useCallback, useEffect, useRef, useState } from 'react'
import { Modal, ModalCloseButton } from '../Modal.js'
import { Button } from '../ui.js'

/**
 * Where a client signs.
 *
 * A canvas and pointer events, not a library. Pointer events are one API for a
 * finger, a stylus and a mouse, which is the whole of what this needs — the
 * office signs off with a trackpad and the client signs with a thumb on the same
 * component.
 *
 * The result is a PNG, uploaded as an ordinary `Attachment` of kind `SIGNATURE`,
 * so it lives where every other file on the job lives and the download route
 * already knows how to serve it. Nothing is stored as a data URL in a column.
 */
export function SignaturePad({
  open,
  title,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  title: string
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (png: Blob) => void
}) {
  const canvas = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const [marked, setMarked] = useState(false)

  // Sized from the element's own box rather than fixed, so the strokes land under
  // the finger on a phone and are not stretched on a desk. Backing store is at
  // device resolution or the signature reads as a blurry smear on a retina
  // screen.
  const fit = useCallback(() => {
    const element = canvas.current
    if (!element) return

    const box = element.getBoundingClientRect()
    const ratio = window.devicePixelRatio || 1
    element.width = Math.max(1, Math.round(box.width * ratio))
    element.height = Math.max(1, Math.round(box.height * ratio))

    const context = element.getContext('2d')
    if (!context) return

    context.scale(ratio, ratio)
    context.lineWidth = 2
    context.lineCap = 'round'
    context.lineJoin = 'round'
    context.strokeStyle = '#0f172a'
  }, [])

  useEffect(() => {
    if (!open) return
    setMarked(false)
    // One frame later, so the canvas has been laid out and has a box to measure.
    const frame = requestAnimationFrame(fit)
    return () => cancelAnimationFrame(frame)
  }, [open, fit])

  const at = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect()
    return { x: event.clientX - box.left, y: event.clientY - box.top }
  }

  const clear = () => {
    const element = canvas.current
    const context = element?.getContext('2d')
    if (element && context) context.clearRect(0, 0, element.width, element.height)
    setMarked(false)
  }

  const save = () => {
    const element = canvas.current
    if (!element) return

    // White behind the strokes: a transparent PNG on a printed report is an
    // empty box, and a signature that does not appear is worse than none.
    const flat = document.createElement('canvas')
    flat.width = element.width
    flat.height = element.height
    const context = flat.getContext('2d')

    if (!context) {
      onSave(new Blob([], { type: 'image/png' }))
      return
    }

    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, flat.width, flat.height)
    context.drawImage(element, 0, 0)

    flat.toBlob((blob) => {
      if (blob) onSave(blob)
    }, 'image/png')
  }

  return (
    <Modal open={open} onClose={onClose} labelledBy="signature-title" className="max-w-[520px]">
      <div className="flex items-start gap-3 border-b border-ink-200 px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <h2 id="signature-title" className="text-[15px] font-semibold text-ink-900">
            {title}
          </h2>
          <p className="mt-0.5 text-xs text-ink-500">Sign in the box with a finger or a mouse.</p>
        </div>
        <ModalCloseButton onClose={onClose} />
      </div>

      <div className="flex flex-col gap-3 p-4">
        <canvas
          ref={canvas}
          aria-label={title}
          role="img"
          className="h-[180px] w-full touch-none rounded-[10px] bg-white ring-1 ring-ink-300 ring-inset"
          onPointerDown={(event) => {
            const context = event.currentTarget.getContext('2d')
            if (!context) return
            event.currentTarget.setPointerCapture(event.pointerId)
            drawing.current = true
            const { x, y } = at(event)
            context.beginPath()
            context.moveTo(x, y)
          }}
          onPointerMove={(event) => {
            if (!drawing.current) return
            const context = event.currentTarget.getContext('2d')
            if (!context) return
            const { x, y } = at(event)
            context.lineTo(x, y)
            context.stroke()
            setMarked(true)
          }}
          onPointerUp={() => {
            drawing.current = false
          }}
          onPointerCancel={() => {
            drawing.current = false
          }}
        />

        {error ? <p className="text-xs text-red-700">{error}</p> : null}

        <div className="flex items-center gap-2.5">
          <Button onClick={clear}>Clear</Button>
          <Button
            variant="primary"
            size="lg"
            block
            loading={saving}
            disabled={!marked}
            onClick={save}
            className="rounded-full"
          >
            Done
          </Button>
        </div>
      </div>
    </Modal>
  )
}
